import { useApolloClient, type ApolloClient } from '@apollo/client';
import { CLIENT_MESSAGE_TYPE } from '@orn/global/assistant/wireProtocol.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import {
  debugLog,
  describeCloseEvent,
  getReconnectAfterCloseDecision,
  isAssistantPageActive,
} from '../lib/assistant/connectionLifecycle';
import { generateRowId } from '../lib/assistant/rowIds';
import {
  appendStreamingDelta,
  confirmPendingMessages,
  finalizeStreamingBubble,
  insertAssistantReply,
  insertToolCall,
  insertWelcomeRow,
  reconcileSnapshotMessages,
  snapshotToConversation,
  stampToolCallWorkItemMove,
} from '../lib/assistantSnapshot';
import { getWebSocketUrl } from '../lib/getEnv';

import { useAssistantReconnect } from './assistant/useAssistantReconnect';
import { useAssistantTurnLifecycle } from './assistant/useAssistantTurnLifecycle';

import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';
import type { IInvokeClientTool, ServerMessage } from '@orn/global/assistant/wireProtocol.js';
import type { IWorkItemMoveFacts } from '@orn/global/assistant/workItemMove.js';

/**
 * Refresh the app's Apollo cache in response to a `data_changed` frame. The
 * assistant writes through its own tool channel, which never touches the app's
 * normalized cache, so panes (e.g. obligations) would otherwise show data the
 * assistant has already mutated until a manual refresh.
 *
 * A bare `refetchQueries` can't fix this on its own: an unmounted pane's
 * `useQuery` is torn down and dropped from Apollo's query registry, so neither
 * `'active'` nor `'all'` reaches it — and on remount a `cache-first` read serves
 * the stale entry. So evict the root-query cache and `gc()` first: a stale entry
 * can no longer survive to be read on remount, and mounted panes are then
 * refetched in place (no loading flash for visible data). Coarse and heavy by
 * design (wipes every root-query field on every write); the durable replacement
 * is a `data_changed` payload carrying the affected entity type so only those
 * cache regions are evicted. Fire-and-forget: a failed refetch is logged.
 */
export function refreshCacheAfterAssistantWrite(client: ApolloClient<object>): void {
  debugLog('data_changed — evicting root cache and refetching active queries');
  client.cache.evict({ id: 'ROOT_QUERY' });
  client.cache.gc();
  void client.refetchQueries({ include: 'active' }).catch((err: unknown) => {
    logger.warn(`[assistant-ws] failed to refetch queries after data_changed: ${String(err)}`);
  });
}

// --- UI types ---

/**
 * A single turn in the rendered conversation transcript (user OR assistant).
 * Distinct from backend's `IAssistantMessage` wire envelope, which only carries
 * the assistant's content.
 *
 * Three independent fields describe a user bubble's state machine:
 *  - `clientMessageId` — durable identity. Set on every user message the
 *    client originates (UUID at send time) and echoed back on
 *    `transcript_snapshot.user.clientMessageId` for snapshot-derived rows.
 *    Used by the snapshot reconciler to dedupe by id.
 *  - `isPending` — true from send until the matching `assistant_message`
 *    arrives. Cleared on confirmation. The snapshot reconciler preserves
 *    only pending bubbles across replays.
 *  - `isInFlight` — true when the server is actively processing this
 *    bubble's turn; false when the bubble is queued locally waiting for the
 *    previous turn to complete (mid-turn typing). The view renders
 *    in-flight bubbles ABOVE the thinking box and queued bubbles BELOW it,
 *    so a user typing follow-ups while the assistant is replying sees the
 *    layout reflect "this is what I'm waiting on" vs "this hasn't started
 *    yet". Only meaningful while `isPending` is true.
 *
 * Splitting these avoids overloading one field with multiple meanings —
 * snapshot-derived messages carry a `clientMessageId` (durable) but are
 * never pending or in-flight.
 */
export interface IConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  clientMessageId?: string;
  isPending?: boolean;
  isInFlight?: boolean;
  /**
   * True while this assistant bubble is being grown token-by-token from
   * `assistant_message_delta` frames, before its round's terminal
   * `assistant_message` snaps it to the authoritative text. The only home for
   * "a bubble is mid-stream": the view derives the inline-vs-standalone spinner
   * from it, and `endTurn` drops any bubble still carrying it. Assistant-only;
   * never persisted, never on the wire.
   */
  isStreaming?: boolean;
  /**
   * Documents the user attached on this turn. Populated locally on `sendMessage`
   * from the composer's resolved `documentId` chips, and from
   * `transcript_snapshot.user.attachments` on rehydration. Renders as a chip
   * row above the user bubble in `AssistantMessage`.
   */
  attachments?: IObservationDocument[];
  /**
   * The alert whose expanded reply offered the suggestion this user bubble
   * was sent from — `content` is the tapped chip's label. Populated locally on
   * `sendMessage` so a supersession requeue can rebuild the wire part from the
   * bubble; never rendered, and snapshots do not carry it (a rehydrated bubble
   * is already confirmed, so nothing rebuilds a wire part from it).
   */
  sourceAlertId?: string;
  /**
   * `'tool-call'` marks a tool-activity line — the model's user-facing goal for
   * one tool call ("Looking for your recent transactions"), rendered as a small
   * gray line OUTSIDE any speech bubble. The goal text rides in `content`
   * (so it flows through `isRenderableMessage` / scroll-follow unchanged); the
   * trailing ellipsis is added at render. Assistant-side and never pending, so
   * it sits with the in-flight turn above queued bubbles. Fed live by
   * `tool_started.goal` and rebuilt from the `tool_call` snapshot row on
   * reconnect. Absent on normal message bubbles.
   *
   * `'alert-card'` marks the alert an expand turn was opened on, rendered as a
   * header above the reply. It carries no text of its own — `alertId` is the
   * whole payload and every string comes from the live work item — so unlike a
   * tool-call line it is renderable with empty `content`. Fed live by the
   * `alert_card` frame and rebuilt from the `alert_card` snapshot row.
   *
   * `'welcome'` marks the deterministic session-opening header ("Welcome
   * back, {greetingName}" + the active-alert count), rendered outside any
   * bubble. Client-local only: minted when a `transcript_snapshot` carries
   * `freshThread`, never sent to the server, never persisted — every string
   * it shows comes from props at render, so like an alert card it is
   * renderable with empty `content`.
   */
  kind?: 'tool-call' | 'alert-card' | 'welcome';
  /** Framework tool-call id for a `'tool-call'` item — its stable render key. */
  toolId?: string;
  /**
   * Call ids of the identical-goal neighbors a `'tool-call'` line absorbed
   * when consecutive calls collapsed into it, so a frame naming one of them
   * still finds the line it renders on.
   */
  absorbedToolIds?: string[];
  /**
   * What each of a `'tool-call'` line's calls left a work item as, once the
   * write succeeded — the frozen facts its move lines render from. Patched in
   * from `tool_completed.workItemMove` live, read from the `tool_call`
   * snapshot row; a collapsed line carries one entry per moved item, last
   * write winning.
   */
  workItemMoves?: IWorkItemMoveFacts[];
  /** The alert an `'alert-card'` item names. Absent on every other kind. */
  alertId?: string;
  /**
   * On a `'welcome'` row: this session is the user's first ever, so the
   * header greets without the "back" a returning user gets. Absent on every
   * other kind.
   */
  isFirstSession?: boolean;
  /**
   * Opaque key grouping the rows of one turn — the persisted `turn_id` on
   * rehydrated rows, a client-minted key on live ones (the two never need to
   * agree: only same-turn equality is read). An optimistic user row is
   * stamped when its turn starts — at send, or at drain for one queued
   * mid-turn — so it is absent only while queued, and on rows from snapshots
   * that predate the field; both read as same-turn.
   */
  turnKey?: string;
}

export interface IToolExecution {
  id: string;
  status: 'running' | 'complete' | 'error';
  /**
   * Tool name, args, and full output. Populated only for viewers who pass the
   * `debug-assistant` gate (the server omits them otherwise), since tool detail
   * is no longer rendered — it is logged to the debug console as each tool
   * completes. Buffered per live turn solely to assemble that log line.
   */
  toolName?: string;
  toolInput?: Record<string, unknown>;
  result?: unknown;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error' | 'superseded';

export interface IUseAssistantWebSocketReturn {
  messages: IConversationMessage[];
  isProcessing: boolean;
  /**
   * True only while the in-flight turn's agent run is live and abortable
   * (from `status:'thinking'` to turn end). Drives the composer's send↔stop
   * morph; false during the non-abortable working window (optimistic send,
   * mid-session compaction). Server-initiated turns (the proactive bootstrap)
   * are abortable too — the stop button morphs in once their run goes live.
   */
  isAbortable: boolean;
  /**
   * True from `ws.onopen` until the server's `assistant_ready` lands —
   * i.e. while the rehydration handshake (`transcript_snapshot` →
   * `assistant_ready`) is in flight. The view can render a spinner during
   * this window; once `messages` populates from the snapshot, the spinner
   * is naturally suppressed.
   */
  isRehydrating: boolean;
  /**
   * Bumped every time the server starts a fresh thread (`/reset`). Per-thread
   * state a consumer keeps outside this hook keys on it to know when to drop —
   * a reset is not a remount, so nothing else tells the pane its transcript is
   * about to be replaced.
   */
  threadGeneration: number;
  connectionStatus: ConnectionStatus;
  error: string | null;
  /**
   * Send a user turn. `sourceAlertId` names the alert whose expanded reply
   * offered the suggestion the user tapped, when the turn is a chip click —
   * see `UserMessagePart.sourceAlertId` for the wire contract.
   */
  sendMessage: (content: string, attachments?: IObservationDocument[], sourceAlertId?: string) => void;
  /**
   * Open an alert in the transcript — the server runs one turn about it and
   * pins a card naming it above the reply. Queued behind an in-flight turn on
   * the same gate as {@link sendMessage}, and in the same FIFO, so an expand
   * clicked after a mid-turn typed message still lands after it.
   *
   * No optimistic row: the card arrives on the server's `alert_card` frame, so
   * the live transcript and a reloaded one are built from the same source.
   */
  expandAlert: (alertId: string) => void;
  /** Interrupt the in-flight turn (sends `stop_turn`). No-op if the WS is closed. */
  stopTurn: () => void;
  /**
   * Re-claim the assistant in this tab after it was superseded by another tab
   * (`connectionStatus === 'superseded'`). Reconnects, which re-stamps the
   * server-side ownership claim so this tab becomes the live one and the other
   * is evicted on its next send. Wired to the "use here" affordance.
   */
  reclaim: () => void;
  /**
   * Client-exposed action tools the server is asking this browser to run (e.g.
   * `financial_account_connect` → the Plaid Link flow), oldest first. A round can
   * carry more than one (the agent framework executes tool calls in parallel),
   * so this is a FIFO queue keyed by `toolId` — every invocation is retained
   * until it's answered, so none is dropped and the server never waits out its
   * timeout. Entries are appended on `invoke_client_tool` and removed on
   * `cancel_client_tool` (the server gave up) or once
   * {@link sendClientToolResult} posts the outcome. The pane runs them one at a
   * time (head of the queue) so two browser flows never overlap.
   */
  pendingClientTools: IInvokeClientTool[];
  /**
   * Return a client tool's outcome to the server as a `client_tool_result`
   * frame, resuming the suspended agent turn that called it. Removes the
   * matching `toolId` from {@link pendingClientTools}. No-op send if the WS is
   * closed (the queue entry is still removed so the runner tears down).
   */
  sendClientToolResult: (toolId: string, result?: unknown, error?: string) => void;
}

/**
 * Custom hook for managing the WebSocket connection to the assistant server.
 *
 * Authentication is cookie-based: the browser attaches the httpOnly `_s`
 * session cookie to the WS upgrade automatically. The hook itself does not
 * receive or know any session credential. Callers pass `isAuthenticated`
 * (a boolean derived from their auth provider) to gate connection — a
 * `false` value keeps the socket disconnected, a `true → false` transition
 * tears it down, and a `false → true` transition reconnects. The server
 * (`packages/api/src/assistant/websocket/assistantWebSocket.ts`) reads `_s` from the
 * upgrade request's `Cookie` header and checks the `Origin` header against
 * `WEBAPP_URL` to defend against cross-site WebSocket hijacking.
 *
 * The server emits `transcript_snapshot` then `assistant_ready` on every
 * connect (and again after `/reset`). The hook gates outbound
 * `user_message` and `/reset` sends on the second frame — the server
 * rejects pre-handshake client messages with an `ASSISTANT_NOT_READY` error
 * that this hook swallows, since recovery is automatic once `assistant_ready`
 * lands.
 *
 * What lives here is the connection and the inbound message switch. The turn
 * queue and the row bookkeeping that keeps the live transcript equal to a
 * rebuilt one live in `useAssistantTurnLifecycle`; the backoff schedule and its
 * recovery signals live in `useAssistantReconnect`.
 *
 * State / ref pairing: several pieces of state have a sibling ref
 * (`isProcessing` ↔ `turnInFlightRef`, `connectionStatus` is read by event
 * callbacks via `wsRef.current.readyState`, etc.). The state drives renders;
 * the ref provides a synchronous read from inside event callbacks where
 * setState has not yet committed.
 */
export const useAssistantWebSocket = (isAuthenticated: boolean): IUseAssistantWebSocketReturn => {
  const [messages, setMessages] = React.useState<IConversationMessage[]>([]);
  // Always-current mirror of `messages` for synchronous reads inside event
  // callbacks whose `messages` closure capture is stale (e.g. the 4009 close
  // handler). Assigned during render — same latest-value-ref idiom this file
  // uses for `connectRef` / `handleServerMessageRef`.
  const messagesRef = React.useRef(messages);
  messagesRef.current = messages;
  // Apollo client mirrored into a ref so the server-message handler (a stable
  // ref callback) can refetch on a `data_changed` frame without the client
  // becoming a dependency — same latest-value-ref idiom as the other refs here.
  const apolloClient = useApolloClient();
  const apolloClientRef = React.useRef(apolloClient);
  apolloClientRef.current = apolloClient;
  const [isProcessing, setIsProcessing] = React.useState(false);
  // True only while the in-flight turn's agent run is LIVE and therefore
  // abortable — i.e. from the server's `status:'thinking'` until the turn ends.
  // Distinct from `isProcessing`, which also covers the working-but-not-yet-
  // abortable window (optimistic send, and the silent mid-session compaction
  // before the agent run starts). Drives the composer's send↔stop morph: the
  // button is a stop button iff `isAbortable`, otherwise a normal send button.
  // Promotes on ANY in-flight turn's `status:'thinking'`, including the
  // server-initiated proactive bootstrap — that greeting is interruptible too.
  const [isAbortable, setIsAbortable] = React.useState(false);
  const [isRehydrating, setIsRehydrating] = React.useState(false);
  const [threadGeneration, setThreadGeneration] = React.useState(0);
  // Start optimistic. On a cold page load the WS upgrade only happens after
  // AuthProvider resolves `session`, and we don't want a "Disconnected" flash
  // covering that window. `connect()` flips to 'disconnected' if auth
  // resolves to logged-out; `ws.onclose` flips it if a real connection drops.
  const [connectionStatus, setConnectionStatus] = React.useState<ConnectionStatus>('connecting');
  const [error, setError] = React.useState<string | null>(null);
  // FIFO queue of client-exposed action tools the server wants this browser to
  // run. State (not a ref) because the pane renders a runner against the head.
  const [pendingClientTools, setPendingClientTools] = React.useState<IInvokeClientTool[]>([]);

  const wsRef = React.useRef<WebSocket | null>(null);
  const isConnectingRef = React.useRef(false);
  const hasConnectedRef = React.useRef(false);
  // Flips true on `assistant_ready` receipt; flips false on every `onclose`
  // and immediately before sending `/reset`. Gates outbound sends.
  const readyRef = React.useRef(false);
  // Set when the socket closes 4009 (superseded by a newer tab). Terminal:
  // gates `sendMessage` from auto-dialing a reconnect — a superseded tab holds
  // its queued messages until the user clicks Reconnect — and is cleared by
  // `reclaim`.
  const supersededRef = React.useRef(false);
  // The session-opening welcome row, latched for the life of the thread it
  // was minted for. Minted when a snapshot carries `freshThread`; re-inserted
  // after a later snapshot only when that snapshot proves the SAME thread
  // (its `threadId` matches the latch's), because snapshot replacement would
  // otherwise evict a header the user is looking at on any transient
  // reconnect — while a mid-session rebind onto a fresh thread (no
  // `thread_reset`, no `freshThread`) must NOT inherit the old thread's
  // welcome. Unproven identity drops the latch. Cleared on `thread_reset` —
  // the rebind's own snapshot mints the next one.
  const welcomeRowRef = React.useRef<{ threadId: string | null; row: IConversationMessage } | null>(null);

  // Declared ahead of everything that dials so the stable `connect` wrapper can
  // be handed to the sub-hooks; the body is assigned further down, once the
  // pieces it drives exist.
  const connectRef = React.useRef<() => void>(() => {});
  const connect = React.useCallback(() => {
    connectRef.current();
  }, []);

  const {
    turnInFlightRef,
    streamingRowIdRef,
    toolExecutionsRef,
    endTurn,
    flushPendingQueue,
    sendUserMessage,
    expandAlert,
    requeueSuperseded,
    trackTurnRow,
    commitTurnRows,
    currentTurnKey,
  } = useAssistantTurnLifecycle({
    wsRef,
    readyRef,
    supersededRef,
    messagesRef,
    connect,
    setMessages,
    setError,
    setIsProcessing,
    setIsAbortable,
  });

  const { scheduleReconnect, deferReconnect, resetBackoff, clearDeferred, cancelScheduledReconnect } =
    useAssistantReconnect({ isAuthenticated, wsRef, supersededRef, connect });

  const handleServerMessageRef = React.useRef<(data: ServerMessage) => void>(() => {});
  handleServerMessageRef.current = (data: ServerMessage) => {
    switch (data.type) {
      case 'status':
        if (data.status === 'thinking') {
          setIsProcessing(true);
          // Gate user sends from this point — the server has signaled
          // that a turn is in flight. Necessary for SERVER-INITIATED
          // turns (the proactive session bootstrap is one): without this
          // flip, the send gate would be open and a user typing
          // mid-bootstrap would ship their message immediately,
          // landing the optimistic bubble above the still-pending
          // assistant reply. For client-initiated turns this is a no-op
          // — `startTurn` already flipped the ref true before the
          // user-frame went out.
          turnInFlightRef.current = true;
          // The agent run is now live → the turn is abortable. `thinking` fires
          // after any silent mid-session compaction, so this also closes the
          // non-abortable compaction window. Server-initiated turns (the
          // proactive bootstrap) promote here too — their greeting is
          // interruptible via the stop button, just like a user turn.
          setIsAbortable(true);
        }
        break;

      case 'tool_started': {
        const newTool: IToolExecution = {
          id: data.toolId,
          toolName: data.toolName,
          toolInput: data.toolInput,
          status: 'running' as const,
        };
        toolExecutionsRef.current = [...toolExecutionsRef.current, newTool];
        // Surface the model's user-facing goal as a gray tool-activity line.
        // Insert it with the in-flight turn (above any mid-turn queued bubbles)
        // where it persists for the rest of the session; the round's streaming
        // reply lands after it. Omitted entirely when the call carried no goal.
        if (typeof data.goal === 'string' && data.goal.length > 0) {
          const toolCallItem: IConversationMessage = {
            id: `tool-call-${data.toolId}`,
            role: 'assistant',
            kind: 'tool-call',
            toolId: data.toolId,
            content: data.goal,
            timestamp: new Date(),
            turnKey: currentTurnKey(),
          };
          setMessages((prev) => insertToolCall(prev, toolCallItem));
          trackTurnRow(toolCallItem.id);
        }
        break;
      }

      case 'tool_completed': {
        if (data.workItemMove !== undefined) {
          const workItemMove = data.workItemMove;
          setMessages((prev) => stampToolCallWorkItemMove(prev, data.toolId, workItemMove));
        }
        const idx = toolExecutionsRef.current.findIndex((t) => t.id === data.toolId && t.status === 'running');
        if (idx >= 0) {
          const tool: IToolExecution = {
            ...toolExecutionsRef.current[idx],
            status: 'complete',
            result: data.result,
          };
          const next = [...toolExecutionsRef.current];
          next[idx] = tool;
          toolExecutionsRef.current = next;
          // The server sends tool name/args/output only to debug-assistant
          // viewers, so a populated `toolName` is the signal to log. One row per
          // tool, the moment it completes.
          if (tool.toolName !== undefined) {
            // eslint-disable-next-line no-console -- the debug-assistant gate exists to surface tool detail in the console
            console.debug(
              '[assistant] %c%s%c tool call',
              'font-weight: bold',
              tool.toolName,
              'font-weight: normal',
              tool
            );
          }
        }
        break;
      }

      case 'assistant_message_delta': {
        const streamingId = streamingRowIdRef.current;
        if (streamingId === null) {
          // First delta of the round: seed a streaming bubble at the same slot a
          // finalized reply would land (so multi-round / mid-turn-typing ordering
          // reuses the one tested insertion path).
          const streamingRow: IConversationMessage = {
            id: `msg-${generateRowId()}`,
            role: 'assistant',
            content: data.text,
            timestamp: new Date(),
            isStreaming: true,
            turnKey: currentTurnKey(),
          };
          setMessages((prev) => insertAssistantReply(prev, streamingRow));
          trackTurnRow(streamingRow.id);
          streamingRowIdRef.current = streamingRow.id;
        } else {
          setMessages((prev) => appendStreamingDelta(prev, streamingId, data.text));
        }
        break;
      }

      case 'assistant_message': {
        const streamingId = streamingRowIdRef.current;
        if (streamingId !== null) {
          // Finalize the bubble grown from this round's deltas: snap to the
          // authoritative full text (drops any client-side accumulation drift)
          // and clear its streaming flag.
          setMessages((prev) => finalizeStreamingBubble(prev, streamingId, data.content));
          streamingRowIdRef.current = null;
        } else {
          // No deltas streamed for this round (e.g. a round that produced text
          // without per-delta events) — create the bubble outright, as before.
          const assistantRow: IConversationMessage = {
            id: `msg-${generateRowId()}`,
            role: 'assistant',
            content: data.content,
            timestamp: new Date(),
            turnKey: currentTurnKey(),
          };
          setMessages((prev) => insertAssistantReply(prev, assistantRow));
          trackTurnRow(assistantRow.id);
        }
        // Round boundary: clear the per-round tool buffer so the next round's
        // tool_completed logs start fresh.
        toolExecutionsRef.current = [];
        break;
      }

      case 'turn_complete': {
        // Terminal frame for the turn. Confirm every pending bubble whose
        // id participated in this turn, then close out turn state and
        // drain any mid-turn typed-message queue as a new batched turn.
        // Server-initiated turns (bootstrap) omit `clientMessageIds`;
        // `confirmPendingMessages` returns input unchanged for an empty
        // array.
        const confirmingIds = data.clientMessageIds ?? [];
        setMessages((prev) => confirmPendingMessages(prev, confirmingIds));
        // Keep this turn's rows only if the turn is actually in history.
        // `turn_complete` alone does not prove that: a stop before the first
        // round, a turn that produced no text, and a swallowed append failure
        // all end the turn this way while the server keeps none of it — so on
        // `discarded` the list stays and `endTurn` takes every row back.
        if (data.discarded !== true) {
          commitTurnRows();
        }
        endTurn();
        // No-op if the queue is empty; otherwise starts the next turn
        // immediately, flipping isProcessing back to true in the same
        // React batch so the spinner doesn't flicker.
        flushPendingQueue();
        break;
      }

      case 'error':
        // Swallow `ASSISTANT_NOT_READY` — the server rejected a frame that
        // raced the handshake. Recovery is automatic once the next
        // `assistant_ready` lands and the FIFO queue flushes. Surfacing the
        // raw protocol message to a user who did nothing wrong is noise.
        if (data.code === 'ASSISTANT_NOT_READY') {
          // eslint-disable-next-line no-console
          console.warn('Assistant rejected pre-handshake message; will retry after assistant_ready', {
            message: data.message,
          });
          break;
        }
        setError(data.message);
        endTurn();
        // An error is as terminal as `turn_complete`, so it drains the queue on
        // the same rule. A rejected `alert_expand` is the short path that makes
        // this reachable — it ends the turn without the orchestrator ever
        // running, so a message typed during that validation window would
        // otherwise wait for an unrelated turn to carry it.
        flushPendingQueue();
        break;

      case 'transcript_snapshot': {
        // Replace messages with snapshot-derived rows, preserving any
        // in-flight optimistic bubbles not represented in the snapshot. The
        // reconciler matches by `clientMessageId` identity.
        const snapshotMessages = snapshotToConversation(data.messages, generateRowId);
        // A fresh-thread bind mints the deterministic welcome header. The row
        // is client-local (snapshots never carry it), so it is latched and
        // re-inserted after every proven-same-thread snapshot below —
        // otherwise a transient reconnect's replacement would evict a header
        // the user is looking at. A snapshot for a DIFFERENT thread — the
        // mid-session expired-thread rebind, which flags nothing — drops the
        // latch instead: the old thread's welcome must not migrate into its
        // replacement. Identity is the snapshot's `threadId`; without one on
        // both sides the same-thread claim is unproven and the latch drops.
        if (data.freshThread === true) {
          welcomeRowRef.current = {
            threadId: data.threadId ?? null,
            row: {
              id: `welcome-${generateRowId()}`,
              role: 'assistant',
              kind: 'welcome',
              content: '',
              timestamp: new Date(),
              ...(data.isFirstSession === true ? { isFirstSession: true } : {}),
            },
          };
        } else if (
          welcomeRowRef.current !== null &&
          (welcomeRowRef.current.threadId === null || welcomeRowRef.current.threadId !== data.threadId)
        ) {
          welcomeRowRef.current = null;
        }
        const welcomeRow = welcomeRowRef.current?.row ?? null;
        // The reconciler keeps only pending bubbles, so a mid-stream streaming
        // bubble (not pending) is already evicted here; the `endTurn` below then
        // clears the turn state, and its row retraction is a harmless no-op —
        // the ids it holds name rows this replaced list no longer contains.
        setMessages((prev) => {
          const next = reconcileSnapshotMessages(prev, snapshotMessages, data.messages);
          return welcomeRow === null ? next : insertWelcomeRow(next, welcomeRow);
        });
        // Any prior connection's live-stream state is gone — a snapshot
        // arriving mid-turn after a reconnect must not leave a stale
        // "Executing tool..." spinner on screen.
        endTurn();
        break;
      }

      case 'thread_reset':
        // Drop only pending optimistic bubbles (the typed `/reset`); keeping
        // confirmed history on screen avoids an empty-list scroll jump before the
        // rebind's snapshot lands. Gate sends until the next `assistant_ready`.
        readyRef.current = false;
        // The welcome latch dies with its thread — the rebind's snapshot
        // carries `freshThread` and mints the next header itself.
        welcomeRowRef.current = null;
        setMessages((prev) => prev.filter((m) => m.isPending !== true));
        setThreadGeneration((generation) => generation + 1);
        endTurn();
        break;

      case 'assistant_ready':
        readyRef.current = true;
        setIsRehydrating(false);
        // Drain any messages the user typed while we were reconnecting /
        // pre-handshake. The drain coalesces multi-entry queues into one
        // `user_message` frame so the LLM sees them as one turn.
        flushPendingQueue();
        break;

      case 'invoke_client_tool':
        // The model called a client-exposed action tool (e.g.
        // financial_account_connect → Plaid Link). Enqueue it for the pane's runner
        // to execute in the browser; the runner posts the outcome back via
        // sendClientToolResult, resuming the suspended agent turn. Dedupe by
        // toolId so a redelivered frame doesn't double-enqueue.
        setPendingClientTools((cur) => (cur.some((t) => t.toolId === data.toolId) ? cur : [...cur, data]));
        break;

      case 'cancel_client_tool':
        // Server abandoned the invocation (user stopped the turn, or the
        // round-trip timed out). Drop it so the runner unmounts and tears down
        // any open flow; no result is sent (the server is no longer waiting).
        setPendingClientTools((cur) => cur.filter((t) => t.toolId !== data.toolId));
        break;

      case 'data_changed':
        // The assistant performed a write through its own tool channel. Evict
        // the query cache and refetch so the rest of the app (e.g. obligations
        // pane) re-reads instead of showing stale data — including panes that
        // are currently unmounted.
        refreshCacheAfterAssistantWrite(apolloClientRef.current);
        break;

      case 'alert_card': {
        // Opens an alert-expand turn, ahead of the reply it introduces. Sits
        // with the in-flight turn (above anything queued mid-turn) on the same
        // slot rule as a tool-call line, so the card, the activity lines, and
        // the answer render in arrival order.
        const card: IConversationMessage = {
          id: `alert-card-${generateRowId()}`,
          role: 'assistant',
          kind: 'alert-card',
          alertId: data.alertId,
          content: '',
          timestamp: new Date(),
          turnKey: currentTurnKey(),
        };
        setMessages((prev) => insertAssistantReply(prev, card));
        trackTurnRow(card.id);
        break;
      }

      default:
        break;
    }
  };

  connectRef.current = () => {
    if (!isAuthenticated || isConnectingRef.current) {
      if (!isAuthenticated) {
        setConnectionStatus('disconnected');
      }
      return;
    }

    // Skip the WS construction when the browser knows it's offline. The
    // `online` listener calls back here the moment the network returns.
    // `navigator.onLine === false` is authoritative; `true` is best-effort, so
    // we still attempt and let the real connection succeed or fail on its own.
    if (!navigator.onLine) {
      debugLog('connect skipped — browser is offline; waiting for `online` event');
      setConnectionStatus('disconnected');
      return;
    }

    isConnectingRef.current = true;
    readyRef.current = false;

    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    setConnectionStatus('connecting');
    setError(null);

    try {
      // Auth comes from the `_s` cookie attached by the browser; the server
      // also enforces an Origin allowlist on the upgrade. The user's timezone
      // is no longer passed on the URL — the backend reads it from the user
      // record, which `useTimezoneSync` keeps current on every app load.
      const wsUrl = getWebSocketUrl('/ws/assistant');

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        hasConnectedRef.current = true;
        clearDeferred();
        resetBackoff();
        setConnectionStatus('connected');
        setError(null);
        // Rehydration handshake (`transcript_snapshot` → `assistant_ready`)
        // starts now; the view renders a spinner while this is in flight on
        // a cold connect (suppressed once messages populate from the
        // snapshot). Sending is gated on `assistant_ready`; the queue
        // drains from `handleServerMessageRef` when the handshake completes.
        setIsRehydrating(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as ServerMessage;
          handleServerMessageRef.current(data);
        } catch {
          // Silently ignore malformed messages
        }
      };

      ws.onclose = (event) => {
        debugLog('socket closed', describeCloseEvent(event));
        isConnectingRef.current = false;
        readyRef.current = false;
        setConnectionStatus('disconnected');
        setIsRehydrating(false);
        // `endTurn` hands back the expand this turn was opened on as it clears
        // it, so the supersession branch below can still name it.
        const supersededExpandId = endTurn();

        if (event.code === 4009) {
          // Superseded by a newer tab. Distinct terminal state (not an error):
          // the view shows a Reconnect affordance that calls `reclaim`. The
          // turn the user just sent was rejected server-side before it
          // persisted, so it is put back at the front of the queue for the
          // reclaim to re-send.
          setConnectionStatus('superseded');
          supersededRef.current = true;
          requeueSuperseded(supersededExpandId);
        } else if (event.code === 4001) {
          setError('Authentication failed');
        } else if (event.code === 4003) {
          // The server emits 4003 when the account lacks alpha access.
          setError('Assistant access not enabled');
        }

        const reconnectDecision = getReconnectAfterCloseDecision(event, isAssistantPageActive(document));
        if (reconnectDecision === 'schedule') {
          scheduleReconnect();
        } else if (reconnectDecision === 'defer') {
          deferReconnect();
          debugLog('unexpected close deferred until page is active');
        }
      };

      ws.onerror = () => {
        isConnectingRef.current = false;
        // Only show error if we had a connection that dropped — not during
        // initial connection.
        if (hasConnectedRef.current) {
          setConnectionStatus('error');
          setError('Connection error');
        }
      };
    } catch (err) {
      isConnectingRef.current = false;
      setConnectionStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to connect');
    }
  };

  /**
   * Interrupt the in-flight turn. Sends the `stop_turn` control frame; the
   * server aborts the live agent run, persists the completed rounds, and emits
   * the turn's terminal `turn_complete` — which runs the normal `endTurn` +
   * queue-drain path, so any messages the user queued while the turn ran flush
   * as the next turn. No optimistic local state change: the button is only a
   * stop button while `isAbortable`, and `turn_complete` clears that.
   */
  const stopTurn = React.useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: CLIENT_MESSAGE_TYPE.StopTurn }));
    }
  }, []);

  /**
   * Return a client tool's outcome to the server. Sends the `client_tool_result`
   * frame the suspended agent turn is waiting on, then clears the matching
   * pending invocation so the pane's runner unmounts. No-op send if the WS is
   * closed (the pending state is still cleared so the runner tears down).
   */
  const sendClientToolResult = React.useCallback((toolId: string, result?: unknown, error?: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: CLIENT_MESSAGE_TYPE.ClientToolResult, toolId, result, error }));
    }
    setPendingClientTools((cur) => cur.filter((t) => t.toolId !== toolId));
  }, []);

  /**
   * Re-claim the assistant in this tab after a 4009 supersession. Reset the
   * backoff counter and reconnect; the fresh connection re-stamps the
   * server-side ownership claim, making this the live tab. Manual only — never
   * automatic — so two tabs can't ping-pong evicting each other.
   */
  const reclaim = React.useCallback(() => {
    supersededRef.current = false;
    resetBackoff();
    connectRef.current();
  }, [resetBackoff]);

  // Connect on mount and when session token changes.
  //
  // The WS construction is deferred to a microtask so React 18 StrictMode's
  // synchronous setup → cleanup → setup double-invoke does not create a
  // throwaway WebSocket. Without the defer, the first setup constructs a WS
  // in CONNECTING state, the immediate cleanup calls `ws.close(1000)` — but
  // the browser ignores the close arg while CONNECTING and reports the
  // close as code 1006 (abnormal). That trips the `event.code !== 1000`
  // reconnect branch in `ws.onclose` and a zombie 3-second reconnect timer
  // races the legitimate connection from the second setup, producing two
  // simultaneous server-side WS sessions for the same user.
  React.useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) {
        return;
      }
      connectRef.current();
    });

    return () => {
      cancelled = true;
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      // Ordered after the close so it sweeps up a retry armed by a `close()`
      // that dispatched `onclose` synchronously. A browser dispatches it
      // asynchronously, so a socket closed while CONNECTING still reports 1006
      // after this returns and can arm a timer nothing here clears — this line
      // is not a guard against that case.
      cancelScheduledReconnect();
    };
  }, [isAuthenticated, cancelScheduledReconnect]);

  return {
    messages,
    isProcessing,
    isAbortable,
    isRehydrating,
    threadGeneration,
    connectionStatus,
    error,
    sendMessage: sendUserMessage,
    expandAlert,
    stopTurn,
    reclaim,
    pendingClientTools,
    sendClientToolResult,
  };
};
